// ─── Shared API response types ────────────────────────────────────────────────

export interface PagedResponse<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
}

// ─── Error helper ─────────────────────────────────────────────────────────────

export function extractError(err: unknown, fallback = 'An error occurred'): string {
  if (err && typeof err === 'object') {
    const e = err as Record<string, unknown>;
    const data = e['response'] as Record<string, unknown> | undefined;
    const msg = (data?.['data'] as Record<string, unknown>)?.['error'];
    if (typeof msg === 'string') return msg;
    // Also check response.data.message (some backends use this shape)
    const msg2 = (data?.['data'] as Record<string, unknown>)?.['message'];
    if (typeof msg2 === 'string') return msg2;
    if (typeof e['message'] === 'string') return e['message'];
  }
  return fallback;
}

// ─── Customer types (shared between CustomersPage and CustomerDetailPage) ─────

/** Minimal customer shape returned by the list API. */
export interface CustomerListItem {
  id: string;
  externalId: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  status: string;
  createdAt: string;
}

/** Full customer detail shape returned by GET /customers/:id. */
export interface CustomerDetail extends CustomerListItem {
  updatedAt: string;
  metadata: Record<string, unknown>;
  // Extended profile
  dateOfBirth: string | null;
  gender: string | null;
  language: string | null;
  country: string | null;
  city: string | null;
  timezone: string | null;
  avatarUrl: string | null;
  acquisitionSource: string | null;
  acquisitionDate: string | null;
  company: string | null;
  jobTitle: string | null;
  // Address (v25_customer_expand)
  addressLine1: string | null;
  addressLine2: string | null;
  postalCode: string | null;
  stateProvince: string | null;
  // Enriched by GET /:id
  contacts?: CustomerContact[];
  consents?: CustomerConsent[];
  attributes?: Record<string, string>;
  loyaltyAccounts?: CustomerLoyaltyAccount[];
  segments?: CustomerSegment[];
  identities?: CustomerIdentity[];
  tags?: string[];
}

export interface EngagementScore {
  score: number;
  breakdown: {
    loyalty: number;
    messages: number;
    events: number;
    automations: number;
  };
}

export interface CustomerContact {
  id: string;
  email: string | null;
  phone: string | null;
  isPrimary: boolean;
  emailVerifiedAt: string | null;
  phoneVerifiedAt: string | null;
}

export interface CustomerConsent {
  id: string;
  channel: string;
  status: string;
  source: string | null;
  capturedAt: string;
  expiresAt: string | null;
}

export interface CustomerLoyaltyAccount {
  id: string;
  programId: string;
  programName: string | null;
  pointsBalance: number;
  lifetimePoints: number;
  tierCode: string | null;
  enrolledAt: string;
}

export interface CustomerSegment {
  segmentId: string;
  name: string;
  type: string;
  enteredAt: string;
}

export interface CustomerIdentity {
  id: string;
  identityType: string;
  identityValue: string;
  createdAt: string;
}

/** Helper: full display name, falls back to '—'. */
export function customerFullName(c: Pick<CustomerListItem, 'firstName' | 'lastName'>): string {
  return [c.firstName, c.lastName].filter(Boolean).join(' ') || '—';
}
